import { useCallback, useEffect, useRef, useState } from 'react';
let csrf = '';
export function setCSRF(value: string) { csrf = value; }
export class APIError extends Error {
    constructor(public status: number, public code: string, message: string, public details?: Record<string, unknown>) { super(message); }
}
export async function api<T>(path: string, options: {
    method?: string;
    body?: unknown;
    signal?: AbortSignal;
} = {}): Promise<T> {
    const response = await fetch(path, { method: options.method ?? 'GET', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', ...(csrf ? { 'X-CSRF-Token': csrf } : {}) }, body: options.body === undefined ? undefined : JSON.stringify(options.body), signal: options.signal });
    let data;
    try {
        data = await response.json();
    }
    catch {
        throw new Error('The server returned an unreadable response. Check that the API is running.');
    }
    if (!response.ok) {
        if (response.status === 401 && path !== '/api/auth/login')
            window.dispatchEvent(new Event('relay:unauthenticated'));
        throw new APIError(response.status, data.error?.code ?? 'ERROR', data.error?.message ?? 'Request failed.', data.error?.details);
    }
    return data as T;
}
export function useResource<T>(url: string | null, refreshKey: unknown = 0, interval = 0) {
    const [state, setState] = useState<{
        url: string | null;
        data?: T;
        error?: string;
        loading: boolean;
    }>({ url: null, loading: true });
    const [revision, setRevision] = useState(0);
    const refresh = useCallback(() => setRevision(x => x + 1), []);
    const seq = useRef(0);
    useEffect(() => {
        if (!url)
            return;
        const controller = new AbortController();
        let active = true;
        let loading = false;
        const load = async () => {
            if (loading)
                return;
            loading = true;
            const current = ++seq.current;
            try {
                const data = await api<T>(url, { signal: controller.signal });
                if (active && current === seq.current)
                    setState({ url, data, loading: false });
            }
            catch (e) {
                if (active && !controller.signal.aborted)
                    setState(old => ({ url, data: old.url === url ? old.data : undefined, error: e instanceof Error ? e.message : 'Unable to connect.', loading: false }));
            }
            finally {
                loading = false;
            }
        };
        void load();
        const timer = interval ? setInterval(() => {
            if (document.visibilityState === 'visible')
                void load();
        }, interval) : undefined;
        return () => { active = false; controller.abort(); clearInterval(timer); };
    }, [url, refreshKey, revision, interval]);
    return { ...(state.url === url ? state : { data: undefined, error: undefined, loading: !!url }), refresh };
}
